import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { loadAssignmentsForProject } from 'store/assignments/thunks/loadAssignmentsForProject';
import { loadAssignmentsForProjects } from 'store/assignments/thunks/loadAssignmentsForProjects';
import { loadProjectAndSubProjectAssignments } from 'store/assignments/thunks/loadProjectAndSubProjectAssignments';
import * as filepermissionActions from 'store/filepermissions/actions/filepermissions.actions';
import { editRecords } from 'store/records/thunks/editRecords.thunk';
import { joinExistingEmployeeToProject } from 'store/resources/thunks/joinExistingEmployeeToProject.thunk';
import * as demoActions from './store/actions/demoActions';
import {
  assignmentsAdapter,
  filePermissionsAdapter,
  workDemoEmployersAdapter,
  workDemoProjectsAdapter,
  workDemoResourcesAdapter,
} from './store/selectors/workDemoSliceSelectors';
import { initWorkCalendar } from './store/thunks/initWorkCalendar.thunk';
import { loadAdminCalendarDataInTimeframeWithSearchParams } from './store/thunks/loadAdminCalendarDataInTimeframeWithSearchParams.thunk';
import { loadAdminCalendarFilesDataInTimeframe } from './store/thunks/loadAdminCalendarFilesDataInTimeframe.thunk';
import { loadAdminInitialDataInTimeFrame } from './store/thunks/loadAdminInitialDataInTimeFrame';
import { loadCalendarDataInTimeframe } from './store/thunks/loadCalendarDataInTimeframe.thunk';
import { loadInitialCalendarData } from './store/thunks/loadInitialCalendarData.thunk';
import { loadInitialCalendarDataForAdmin } from './store/thunks/loadInitialCalendarDataForAdmin.thunk';
import { loadSubcontractorEmployeeData } from './store/thunks/loadSubcontractorEmployeeData.thunk';
import { initMyLixaniWorkCalendar } from './store/thunks/my-lixani/initMyLixaniWorkCalendar.thunk';
import { loadPersonalWorkCalendarData } from './store/thunks/my-lixani/loadPersonalWorkCalendarData.thunk';
import { searchWorkCalendar } from './store/thunks/searchWorkCalendar.thunk';
import { setOpenNextWeek } from './store/thunks/setOpenNextWeek.thunk';
import { setOpenPreviousWeek } from './store/thunks/setOpenPreviousWeek.thunk';
import { approveMultipleRecords } from 'store/records/thunks/approveMultipleRecords.thunk';

const initialState = {
  loading: false,
  filePermissions_demo: filePermissionsAdapter.getInitialState(),
  assignments_demo: assignmentsAdapter.getInitialState(),
  projectsDataLoading: false,
  isPersonalWorkCalendarMode: false,
  resources: workDemoResourcesAdapter.getInitialState(),
  employers: workDemoEmployersAdapter.getInitialState(),
  projects: workDemoProjectsAdapter.getInitialState(),
  userProfileDialogData: {},
  isConfirmApprovalDialogOpen: false,
};

const workCalendarSlice = createSlice({
  name: 'workCalendar',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(initWorkCalendar.pending, (state) => ({
        ...state,
        loading: true,
        projectsDataLoading: true,
      }))
      .addCase(demoActions.resetWorkCalendar, () => ({ ...initialState }))
      .addCase(loadInitialCalendarData.fulfilled, (state, action) => {
        state.loading = false;

        filePermissionsAdapter.setMany(
          state.filePermissions_demo,
          action.payload.filePermissions
        );
        assignmentsAdapter.setMany(
          state.assignments_demo,
          action.payload.assignments
        );

        const resources = action.payload.resources;

        const employers = action.payload.employers;

        const projects = action.payload.projects.filter(
          (project) => !project.deletedAt
        );

        workDemoProjectsAdapter.upsertMany(state.projects, projects);
        workDemoEmployersAdapter.upsertMany(state.employers, employers);
        workDemoResourcesAdapter.upsertMany(state.resources, resources);

        state.projectsDataLoading = false;
      })
      .addCase(loadInitialCalendarDataForAdmin.fulfilled, (state, action) => {
        const resources = action.payload.resources.filter(
          (resource) => resource && resource.id
        );
        const employers = action.payload.employers.filter(
          (employer) => employer && employer.id
        );
        const projects = action.payload.externalProjects.filter(
          (project) => project && project.id && !project.deletedAt
        );

        workDemoProjectsAdapter.upsertMany(state.projects, projects);
        workDemoEmployersAdapter.upsertMany(state.employers, employers);
        workDemoResourcesAdapter.upsertMany(state.resources, resources);

        state.projectsDataLoading = false;
      })
      .addCase(loadSubcontractorEmployeeData.fulfilled, (state, action) => {
        const { resources, employers } = action.payload;

        workDemoProjectsAdapter.setMany(state.employers, employers);

        workDemoResourcesAdapter.setMany(state.resources, resources);
      })
      .addCase(loadPersonalWorkCalendarData.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(loadPersonalWorkCalendarData.fulfilled, (state, action) => {
        state.loading = false;
        filePermissionsAdapter.setMany(
          state.filePermissions_demo,
          action.payload.filePermissions
        );
      })
      .addCase(loadPersonalWorkCalendarData.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(initMyLixaniWorkCalendar.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(initMyLixaniWorkCalendar.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(initMyLixaniWorkCalendar.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(filepermissionActions.updateFilepermission, (state, action) => {
        filePermissionsAdapter.upsertOne(state.filePermissions_demo, {
          ...action.payload,
        });
        state.updateFilepermissionLoading = false;
      })

      .addCase(initWorkCalendar.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
        projectsDataLoading: false,
      }))
      .addCase(
        loadProjectAndSubProjectAssignments.fulfilled,
        (state, action) => {
          const assignments = action.payload.filter(
            (assignment) => assignment && assignment.id
          );

          assignmentsAdapter.setMany(state.assignments_demo, assignments);
        }
      )
      .addCase(loadAssignmentsForProject.fulfilled, (state, action) => {
        const assignments = action.payload.filter(
          (assignment) => assignment && assignment.id
        );

        assignmentsAdapter.setMany(state.assignments_demo, assignments);
      })
      .addCase(loadAssignmentsForProjects.fulfilled, (state, action) => {
        const assignments = action.payload.filter(
          (assignment) => assignment && assignment.id
        );

        assignmentsAdapter.setMany(state.assignments_demo, assignments);
      })
      .addCase(initWorkCalendar.fulfilled, (state) => ({
        ...state,
        loading: false,
      }))
      .addCase(loadCalendarDataInTimeframe.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadCalendarDataInTimeframe.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.map((projectData) => {
          filePermissionsAdapter.setMany(
            state.filePermissions_demo,
            projectData.filePermissions
          );
        });
      })
      .addCase(searchWorkCalendar.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(
        loadAdminCalendarFilesDataInTimeframe.fulfilled,
        (state, action) => {
          filePermissionsAdapter.setMany(
            state.filePermissions_demo,
            action.payload
          );
        }
      )
      .addCase(
        loadAdminCalendarDataInTimeframeWithSearchParams.pending,
        (state) => ({
          ...state,
          loading: true,
        })
      )
      .addCase(
        loadAdminCalendarDataInTimeframeWithSearchParams.fulfilled,
        (state, action) => {
          state.loading = false;
          action.payload.map((data) =>
            filePermissionsAdapter.setMany(
              state.filePermissions_demo,
              data.filePermissions
            )
          );
        }
      )
      .addCase(
        loadAdminCalendarDataInTimeframeWithSearchParams.rejected,
        (state) => ({
          ...state,
          loading: false,
        })
      )
      .addCase(loadCalendarDataInTimeframe.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(demoActions.setPersonalWorkCalendarMode, (state, action) => {
        state.isPersonalWorkCalendarMode = action.payload;
      })
      .addCase(loadAdminInitialDataInTimeFrame.fulfilled, (state, action) => {
        const resources = action.payload.resources.filter(
          (resource) => resource && resource.id
        );
        const employers = action.payload.employers.filter(
          (employer) => employer && employer.id
        );
        const projects = action.payload.projects.filter(
          (project) => project && project.id && !project.deletedAt
        );
        workDemoProjectsAdapter.upsertMany(state.projects, projects);
        workDemoEmployersAdapter.upsertMany(state.employers, employers);
        workDemoResourcesAdapter.upsertMany(state.resources, resources);
        filePermissionsAdapter.upsertMany(
          state.filePermissions_demo,
          action.payload.filePermissions
        );
      })
      .addCase(editRecords.fulfilled, (state, action) => {
        workDemoResourcesAdapter.setMany(
          state.resources,
          action.payload.resources
        );
      })
      .addCase(demoActions.setUserProfileDialogData, (state, action) => {
        state.userProfileDialogData = action.payload;
      })
      .addCase(demoActions.updateWorkDemoResource, (state, action) => {
        const { id, ...changes } = action.payload;
        workDemoResourcesAdapter.updateOne(state.resources, { id, changes });
      })
      .addCase(joinExistingEmployeeToProject.fulfilled, (state, action) => {
        const resource = action.payload.resource;

        if (resource) {
          workDemoResourcesAdapter.setOne(state.resources, resource);
        }
      })
      .addCase(demoActions.openConfirmApprovalDialog, (state, action) => {
        state.isConfirmApprovalDialogOpen = true;
      })
      .addCase(demoActions.closeConfirmApprovalDialog, (state, action) => {
        state.isConfirmApprovalDialogOpen = false;
      })
      .addCase(approveMultipleRecords.fulfilled, (state, action) => {
        state.isConfirmApprovalDialogOpen = false;
      })
      .addMatcher(
        isAnyOf(
          searchWorkCalendar.pending,
          setOpenPreviousWeek.pending,
          setOpenNextWeek.pending
        ),
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          searchWorkCalendar.fulfilled,
          setOpenPreviousWeek.fulfilled,
          setOpenNextWeek.fulfilled
        ),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export default workCalendarSlice.reducer;
